<script>
import { mapActions } from "vuex";

export default {
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    object: {
      id: null,
      firstName: null,
      lastName: null,
      position: null,
      phone: null, // national 9 digits (v-model of the masked field)
      refToken: null,
      active: true,
    },
    model: true,
    isLoading: false,
    isInited: false,
    copied: false,
    phoneMask: { mask: "99 999 99 99", autoUnmask: true, showMaskOnHover: false, showMaskOnFocus: false, placeholder: "" },
  }),

  computed: {
    isEdit() {
      return !!this.object.id;
    },
    title() {
      return this.isEdit
        ? this.$t("Edit admission employee")
        : this.$t("Add admission employee");
    },
    subtitle() {
      return this.$t("Enter the admission department employee details.");
    },
    headIcon() {
      return this.isEdit ? "mdi-account-edit-outline" : "mdi-account-multiple-plus-outline";
    },
    canSave() {
      const o = this.object;
      return (
        !!(o.firstName && String(o.firstName).trim()) &&
        !!(o.lastName && String(o.lastName).trim()) &&
        !!(o.position && String(o.position).trim()) &&
        !!(o.phone && o.phone.length === 9)
      );
    },
    referralLink() {
      return this.object.refToken
        ? `https://qabul.usat.uz/?ref=${this.object.refToken}`
        : "";
    },
  },

  watch: {
    model() {
      setTimeout(() => {
        this.$emit("close");
      }, 100);
    },
  },

  created() {
    if (this.data?.id) {
      this.isLoading = true;
      this.isInited = false;
      this.EMPLOYEE_BY_ID({ id: this.data.id })
        .then((data) => {
          if (data) {
            const parts = (data.fullName || "").trim().split(/\s+/).filter(Boolean);
            this.object = {
              id: data.id,
              firstName: parts.shift() || "",
              lastName: parts.join(" "),
              position: data.position || "",
              phone: this.toNational(data.phone),
              refToken: data.refToken || null,
              active: data.active,
            };
          }
        })
        .finally(() => {
          this.isLoading = false;
          this.$nextTick(() => {
            this.isInited = true;
          });
        });
    } else {
      this.isInited = true;
    }
  },

  mounted() {
    document.addEventListener("keydown", this.onKeydown);
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
  },

  methods: {
    ...mapActions("admin-referral-employees", ["ADD_EMPLOYEE", "EMPLOYEE_BY_ID"]),

    onKeydown(e) {
      if (e.key === "Escape") this.close();
    },

    reqRule(v) {
      return !!(v && String(v).trim()) || this.$t("This field is required");
    },
    phoneRule(v) {
      return (!!v && v.length === 9) || this.$t("Enter a valid phone number");
    },

    // Parse any stored format (998XXXXXXXXX, +998 ..., national) down to the 9 national digits.
    toNational(phone) {
      const digits = (phone || "").toString().replace(/\D/g, "");
      return digits.length > 9 ? digits.slice(-9) : digits;
    },

    close() {
      this.model = false;
    },

    onSend() {
      if (!this.$refs.form || !this.$refs.form.validate()) return;
      const fullName = `${(this.object.firstName || "").trim()} ${(this.object.lastName || "").trim()}`.trim();
      // Persist the complete international number to match existing records (998XXXXXXXXX).
      const phone = this.object.phone ? `998${this.object.phone}` : "";
      const payload = {
        id: this.object.id,
        fullName,
        position: (this.object.position || "").trim(),
        phone,
        active: this.object.active,
      };
      this.isLoading = true;
      this.ADD_EMPLOYEE(payload)
        .then((status) => {
          if (status != null) {
            this.$emit("refresh");
            this.model = false;
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    async copyLink() {
      const link = this.referralLink;
      if (!link) return;
      try {
        await navigator.clipboard.writeText(link);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = link;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 1500);
    },
  },
};
</script>

<template>
  <v-dialog
    v-model="model"
    persistent
    max-width="600"
    content-class="qx-modal"
    transition="fade-transition"
  >
    <div class="qx">
      <!-- HEADER -->
      <header class="qx-head">
        <div class="qx-head-icon">
          <v-icon size="22">{{ headIcon }}</v-icon>
        </div>
        <div class="qx-head-text">
          <h2 class="qx-title">{{ title }}</h2>
          <p class="qx-sub">{{ subtitle }}</p>
        </div>
        <button type="button" class="qx-close" :aria-label="$t('Cancel')" @click="close">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </header>

      <!-- BODY -->
      <v-form ref="form" class="qx-body" @submit.prevent="onSend">
        <div v-if="!isInited" class="qx-loading">
          <span class="qx-spinner qx-spinner--lg" aria-hidden="true"></span>
        </div>

        <div v-else class="qx-grid">
          <div class="qx-cell">
            <v-text-field
              v-model="object.firstName"
              :label="$t('First name')"
              placeholder="Ali"
              :rules="[reqRule]"
              autocomplete="off"
              outlined
              hide-details="auto"
              color="#00274c"
            />
          </div>
          <div class="qx-cell">
            <v-text-field
              v-model="object.lastName"
              :label="$t('Last name')"
              placeholder="Valiyev"
              :rules="[reqRule]"
              autocomplete="off"
              outlined
              hide-details="auto"
              color="#00274c"
            />
          </div>
          <div class="qx-cell">
            <v-text-field
              v-model="object.position"
              :label="$t('Position')"
              placeholder="Qabul menejeri"
              :rules="[reqRule]"
              autocomplete="off"
              outlined
              hide-details="auto"
              color="#00274c"
            />
          </div>
          <div class="qx-cell">
            <v-text-field
              v-model="object.phone"
              v-inputmask="phoneMask"
              :label="$t('Phone number')"
              placeholder="90 123 45 67"
              prefix="+998"
              :rules="[reqRule, phoneRule]"
              autocomplete="off"
              inputmode="tel"
              outlined
              hide-details="auto"
              color="#00274c"
            >
              <template #prepend-inner>
                <svg class="qx-phone-ic" viewBox="0 0 100 100" width="20" height="20" fill="currentColor" aria-hidden="true">
                  <path d="m89.2050095 65.7478409c-4.6530457-.6818848-17.3913651-7.0359459-21.6505661-3.2916985-2.485054.8241272-9.4584427 6.998661-12.2504616 5.1023598-8.5013123-5.1715164-17.4548111-14.3039703-22.5859108-23.7310219-.7885933-1.4488373-.737608-3.2032318.1350975-4.6030159 1.674221-2.6853867 4.2950974-6.8908005 4.2950974-6.8908005 3.9017715-4.4446011-2.4451752-17.06353-3.1807823-21.824131-.7268677-4.3962474-3.9695473-8.372036-8.8024082-7.9831915-5.6381722.5769296-17.6257721-.3652072-20.7690607 4.7079287-6.1486034 18.0351868 6.5146756 44.1257553 24.6713219 63.4108124 15.4517746 16.4120789 65.5950623 42.2174301 67.4919357 14.8975525-.06353-8.0754471 3.8645782-17.7569504-7.3542709-19.7948303z"/>
                </svg>
              </template>
            </v-text-field>
          </div>

          <!-- edit-only extras -->
          <div v-if="isEdit" class="qx-cell qx-cell--full">
            <div class="qx-switch">
              <span class="qx-switch-label">
                {{ object.active ? $t('Active') : $t('Inactive') }}
              </span>
              <v-switch
                v-model="object.active"
                inset
                hide-details
                class="ma-0 pa-0"
                color="#00274c"
              />
            </div>
          </div>

          <div v-if="isEdit && object.refToken" class="qx-cell qx-cell--full">
            <v-text-field
              :value="referralLink"
              :label="$t('Referral link')"
              readonly
              outlined
              hide-details
              color="#00274c"
              :append-icon="copied ? 'mdi-check' : 'mdi-content-copy'"
              @click:append="copyLink"
            />
            <div v-if="copied" class="qx-copied">{{ $t('Copied') }}</div>
          </div>
        </div>

        <button type="submit" class="d-none" aria-hidden="true"></button>
      </v-form>

      <!-- FOOTER -->
      <footer class="qx-foot">
        <button type="button" class="qx-btn qx-btn--ghost" @click="close">
          {{ $t('Cancel') }}
        </button>
        <button
          type="button"
          class="qx-btn qx-btn--primary"
          :disabled="!canSave || isLoading"
          @click="onSend"
        >
          <span v-if="isLoading" class="qx-spinner" aria-hidden="true"></span>
          {{ isLoading ? $t('Saving') : $t('Save') }}
        </button>
      </footer>
    </div>
  </v-dialog>
</template>

<style lang="scss">
/* Teleported dialog → all selectors are namespaced under .qx-modal (unscoped, but isolated). */
.qx-modal.v-dialog {
  border-radius: 18px !important;
  box-shadow: 0 24px 60px rgba(12, 19, 34, 0.28) !important;
}
.qx {
  background: #fff;
  border-radius: 18px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  animation: qxIn 0.26s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}
@keyframes qxIn {
  from { opacity: 0; transform: translateY(8px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@media (prefers-reduced-motion: reduce) {
  .qx { animation: none; }
}

/* ── Header ── */
.qx-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 26px 26px 10px;
}
.qx-head-icon {
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 13px;
  display: grid;
  place-items: center;
  background: rgba(255, 206, 74, 0.16);
}
.qx-head-icon .v-icon {
  color: #f4a915 !important;
}
.qx-head-text {
  flex: 1;
  min-width: 0;
  padding-top: 1px;
}
.qx-title {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #0c1322;
  line-height: 1.2;
}
.qx-sub {
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: #6b7280;
}
.qx-close {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: all 0.15s ease;
}
.qx-close:hover {
  border-color: #d1d5db;
  background: #f9fafb;
  color: #111318;
}

/* ── Body / grid ── */
.qx-body {
  padding: 14px 26px 6px;
}
.qx-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 14px;
}
.qx-cell--full {
  grid-column: 1 / -1;
}
.qx-loading {
  display: grid;
  place-items: center;
  min-height: 196px;
}

/* Outlined field overrides */
.qx-modal .v-input--outlined > .v-input__control > .v-input__slot {
  min-height: 52px !important;
  border-radius: 12px !important;
  transition: box-shadow 0.18s ease;
}
.qx-modal .v-input--outlined fieldset {
  border-radius: 12px !important;
  border-color: #e3e6ec;
  transition: border-color 0.18s ease;
}
.qx-modal .v-input--outlined:not(.v-input--is-focused):hover fieldset {
  border-color: #cbd2dd;
}
.qx-modal .v-text-field__prefix {
  color: #0c1322;
  font-weight: 600;
  padding-right: 4px;
}
.qx-modal .v-input__prepend-inner .v-icon {
  color: #9aa3b6 !important;
}
/* custom phone SVG in the prepend-inner slot — center it in the field */
.qx-modal .v-input__prepend-inner {
  align-self: center;
  margin-top: 0 !important;
  display: flex;
  align-items: center;
}
.qx-modal .qx-phone-ic {
  display: block;
  color: #9aa3b6;
}
.qx-modal .v-text-field--outlined .v-text-field__details {
  margin-top: 6px;
  padding: 0 4px;
}

/* edit-only blocks */
.qx-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #eef0f4;
  border-radius: 12px;
  padding: 6px 8px 6px 16px;
  background: #fafbfc;
}
.qx-switch-label {
  font-size: 14px;
  font-weight: 700;
  color: #0c1322;
}
.qx-copied {
  color: #16a34a;
  font-size: 12px;
  font-weight: 600;
  margin-top: 6px;
  padding-left: 4px;
}

/* ── Footer / buttons ── */
.qx-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 26px 24px;
}
.qx-btn {
  height: 44px;
  padding: 0 22px;
  border-radius: 12px;
  border: none;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.15s ease, background 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}
.qx-btn--ghost {
  background: #fff;
  border: 1px solid #e5e7eb;
  color: #475467;
}
.qx-btn--ghost:hover {
  background: #f9fafb;
  border-color: #d0d5dd;
}
.qx-btn--primary {
  background: linear-gradient(135deg, #ffe07a, #f4a915);
  color: #1a1304;
  box-shadow: 0 6px 18px rgba(244, 169, 21, 0.35);
}
.qx-btn--primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px rgba(244, 169, 21, 0.42);
  filter: brightness(1.02);
}
.qx-btn--primary:active {
  transform: translateY(0);
}
.qx-btn--primary:disabled {
  opacity: 0.5;
  cursor: default;
  box-shadow: none;
  transform: none;
  filter: none;
}

/* spinner */
.qx-spinner {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(26, 19, 4, 0.3);
  border-top-color: #1a1304;
  animation: qxSpin 0.7s linear infinite;
}
.qx-spinner--lg {
  width: 30px;
  height: 30px;
  border-width: 3px;
  border-color: rgba(244, 169, 21, 0.25);
  border-top-color: #f4a915;
}
@keyframes qxSpin {
  to { transform: rotate(360deg); }
}

/* ── Responsive ── */
@media (max-width: 560px) {
  .qx-grid {
    grid-template-columns: 1fr;
  }
  .qx-head,
  .qx-body,
  .qx-foot {
    padding-left: 18px;
    padding-right: 18px;
  }
  .qx-foot {
    flex-direction: column-reverse;
  }
  .qx-btn {
    width: 100%;
  }
}

/* ── Dark mode ── */
.theme--dark .qx {
  background: #1a1b23;
}
.theme--dark .qx-title {
  color: #e4e5eb;
}
.theme--dark .qx-sub {
  color: #9aa0ad;
}
.theme--dark .qx-close {
  border-color: #2a2c3a;
  background: transparent;
  color: #9aa0ad;
}
.theme--dark .qx-close:hover {
  background: #23252f;
  border-color: #3a3d4d;
  color: #e4e5eb;
}
/* Dark inputs: a raised fill + a clearly visible border so each field reads as
   a distinct surface on the dark card (the old #2f323d border was invisible). */
.theme--dark .qx-modal .v-input--outlined > .v-input__control > .v-input__slot {
  background: #2a2c38;
}
.theme--dark .qx-modal .v-input--outlined fieldset {
  border-color: #565a76;
}
.theme--dark .qx-modal .v-input--outlined:not(.v-input--is-focused):hover fieldset {
  border-color: #6f7493;
}
.theme--dark .qx-modal .v-input--is-focused > .v-input__control > .v-input__slot {
  background: #32343f;
}
.theme--dark .qx-modal .v-input--is-focused fieldset {
  border-color: #ffd451 !important;
}
.theme--dark .qx-modal .v-input--is-focused .v-label {
  color: #ffd451 !important;
}
/* readable value / prefix / placeholder text on the dark fill */
.theme--dark .qx-modal input,
.theme--dark .qx-modal .v-select__selection,
.theme--dark .qx-modal .v-text-field__prefix {
  color: #e8e9f0;
}
.theme--dark .qx-modal input::placeholder,
.theme--dark .qx-modal textarea::placeholder {
  color: #969cb1;
  opacity: 1;
}
.theme--dark .qx-modal .v-label { color: #9aa0ad; }
.theme--dark .qx-modal .qx-phone-ic { color: #9aa0ad; }
.theme--dark .qx-switch {
  background: #20222b;
  border-color: #2a2c3a;
}
.theme--dark .qx-switch-label {
  color: #e4e5eb;
}
.theme--dark .qx-btn--ghost {
  background: transparent;
  border-color: #2a2c3a;
  color: #c2c6d2;
}
.theme--dark .qx-btn--ghost:hover {
  background: #23252f;
  border-color: #3a3d4d;
}
</style>
